import { useState, useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { useNavigate } from 'react-router-dom'
import { productsAPI, categoriesAPI, companiesAPI, productGroupsAPI } from '../services/api'
import { 
  Plus, Search, Filter, Edit, Trash2, Eye, Package, ChevronLeft, ChevronRight,
  SlidersHorizontal, X, DollarSign, Package2, Building2, Tag, Layers3,
  CheckCircle, XCircle, AlertCircle, TrendingUp, Grid3X3, List, Smartphone, Tablet, Monitor
} from 'lucide-react'
import toast from 'react-hot-toast'
import { usePermissions } from '../hooks/usePermissions'
import { createNavigationHelpers } from '../utils/navigationHelpers'
import ConfirmationModal from '../components/ConfirmationModal'

export default function ProductsEnhanced() {
  const [search, setSearch] = useState('')
  const [categoryFilter, setCategoryFilter] = useState('')
  const [companyFilter, setCompanyFilter] = useState('')
  const [productGroupFilter, setProductGroupFilter] = useState('')
  const [stockFilter, setStockFilter] = useState('')
  const [activeFilter, setActiveFilter] = useState('true')
  const [priceRangeFilter, setPriceRangeFilter] = useState('')
  const [quantityFilter, setQuantityFilter] = useState('')
  const [sortBy, setSortBy] = useState('name')
  const [sortOrder, setSortOrder] = useState('asc')
  const [showFilters, setShowFilters] = useState(true)
  const [viewMode, setViewMode] = useState('grid') // 'grid' or 'list'
  const [deleteModalOpen, setDeleteModalOpen] = useState(false)
  const [productToDelete, setProductToDelete] = useState(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(12)
  
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { products: productPerms } = usePermissions()
  const { goToCreate, goToEdit } = createNavigationHelpers('products', navigate)

  // Price ranges for filtering
  const priceRanges = [
    { value: '', label: 'All Prices' },
    { value: '0-50', label: '₹0 - ₹50' },
    { value: '50-100', label: '₹50 - ₹100' },
    { value: '100-500', label: '₹100 - ₹500' },
    { value: '500-1000', label: '₹500 - ₹1000' },
    { value: '1000+', label: '₹1000+' },
  ]

  // Quantity filter options
  const quantityOptions = [
    { value: '', label: 'All Quantities' },
    { value: 'has-quantity', label: 'Has Quantity Info' },
    { value: 'no-quantity', label: 'No Quantity Info' },
  ]

  // Sort options
  const sortOptions = [
    { value: 'name', label: 'Name' },
    { value: 'price', label: 'Price' },
    { value: 'stock_quantity', label: 'Stock' },
    { value: 'created_at', label: 'Date Created' },
    { value: 'updated_at', label: 'Last Updated' },
  ]

  // Items per page options
  const itemsPerPageOptions = [6, 12, 18, 24, 36, 48]

  // Fetch products with enhanced parameters
  const { data: productsData, isLoading, error } = useQuery(
    [
      'products', 
      currentPage, 
      search, 
      categoryFilter, 
      companyFilter, 
      productGroupFilter, 
      stockFilter, 
      activeFilter,
      priceRangeFilter,
      quantityFilter,
      sortBy,
      sortOrder,
      itemsPerPage
    ],
    () => productsAPI.getAll({
      page: currentPage,
      limit: itemsPerPage,
      search,
      category_uuid: categoryFilter,
      company_uuid: companyFilter,
      product_group_uuid: productGroupFilter,
      in_stock: stockFilter,
      is_active: activeFilter,
      price_range: priceRangeFilter,
      has_quantity: quantityFilter,
      sort_by: sortBy,
      sort_order: sortOrder
    }),
    {
      retry: 1,
      onError: (error) => {
        console.error('Products fetch error:', error);
        toast.error('Failed to load products');
      }
    }
  )

  // Fetch categories for filter
  const { data: categoriesData } = useQuery(
    'categories',
    () => categoriesAPI.getAll({ is_active: 'true' }),
    {
      retry: 1,
      onError: (error) => {
        console.error('Categories fetch error:', error);
      }
    }
  )

  // Fetch companies for filter
  const { data: companiesData } = useQuery(
    'companies',
    () => companiesAPI.getAll({ is_active: 'true' }),
    {
      retry: 1,
      onError: (error) => {
        console.error('Companies fetch error:', error);
      }
    }
  )

  // Fetch product groups for selected category (fixed API call)
  const { data: productGroupsData } = useQuery(
    ['product-groups-for-filter', categoryFilter],
    () => productGroupsAPI.getAll({ 
      category_uuid: categoryFilter,
      is_active: 'true' 
    }),
    {
      enabled: !!categoryFilter,
      retry: 1,
      onError: (error) => {
        console.error('Product groups fetch error:', error);
      }
    }
  )

  // Delete product mutation
  const deleteMutation = useMutation(
    (uuid) => productsAPI.delete(uuid),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('products')
        toast.success('Product deleted successfully')
        setDeleteModalOpen(false)
        setProductToDelete(null)
      },
      onError: (error) => {
        const errorMessage = error.response?.data?.message || 'Failed to delete product'
        toast.error(errorMessage)
      }
    }
  )

  // Toggle status mutation
  const toggleStatusMutation = useMutation(
    (uuid) => productsAPI.toggleStatus(uuid),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('products')
        toast.success('Product status updated successfully!')
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to update product status')
      }
    }
  )

  const handleDelete = (product) => {
    setProductToDelete(product)
    setDeleteModalOpen(true)
  }

  const handleConfirmDelete = () => {
    if (productToDelete) {
      console.log('Deleting product:', productToDelete)
      deleteMutation.mutate(productToDelete.uuid)
    }
  }

  const handleToggleStatus = (product) => {
    toggleStatusMutation.mutate(product.uuid)
  }

  const handleEdit = (product) => {
    goToEdit(product.uuid)
  }

  const handleCreate = () => {
    goToCreate()
  }

  const handleCategoryChange = (categoryId) => {
    setCategoryFilter(categoryId)
    setProductGroupFilter('') // Clear product group when category changes
  }

  const handleCompanyChange = (companyId) => {
    setCompanyFilter(companyId)
  }

  const clearFilters = () => {
    setSearch('')
    setCategoryFilter('')
    setCompanyFilter('')
    setProductGroupFilter('')
    setStockFilter('')
    setActiveFilter('')
    setPriceRangeFilter('')
    setQuantityFilter('')
    setSortBy('name')
    setSortOrder('asc')
    setCurrentPage(1)
  }

  // Process data safely
  const products = Array.isArray(productsData?.data?.data) ? productsData.data.data : []
  const pagination = productsData?.data?.pagination || { page: 1, limit: itemsPerPage, total: 0, pages: 1 }
  const categories = Array.isArray(categoriesData?.data?.data) ? categoriesData.data.data : []
  const companies = Array.isArray(companiesData?.data?.data?.companies) ? companiesData.data.data.companies : []
  const productGroups = Array.isArray(productGroupsData?.data?.data) ? productGroupsData.data.data : []

  // Filter statistics
  const filterStats = useMemo(() => {
    return {
      total: pagination.total || 0,
      activeFilters: [
        search && 'Search',
        categoryFilter && 'Category',
        companyFilter && 'Company', 
        productGroupFilter && 'Product Group',
        stockFilter && 'Stock Status',
        activeFilter && activeFilter !== '' && 'Active Status',
        priceRangeFilter && 'Price Range',
        quantityFilter && 'Quantity'
      ].filter(Boolean).length
    }
  }, [search, categoryFilter, companyFilter, productGroupFilter, stockFilter, activeFilter, priceRangeFilter, quantityFilter, pagination.total])

  if (!productPerms.canView) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="text-6xl mb-4">🔒</div>
          <h2 className="text-xl font-semibold text-gray-700 dark:text-gray-300 mb-2">Access Denied</h2>
          <p className="text-gray-600 dark:text-gray-400">You don't have permission to view products.</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors duration-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6 lg:py-8">
        
        {/* Enhanced Header */}
        <div className="mb-6 sm:mb-8">
          <div className="bg-gradient-to-r from-blue-600 via-purple-600 to-indigo-600 rounded-2xl p-6 sm:p-8 text-white shadow-2xl">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 lg:gap-6">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-3">
                  <Package2 className="h-8 w-8 sm:h-10 sm:w-10" />
                  <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold">Products</h1>
                </div>
                <p className="text-blue-100 text-sm sm:text-base lg:text-lg mb-4">
                  Comprehensive product inventory management
                </p>
                <div className="flex flex-wrap items-center gap-4 text-sm">
                  <div className="flex items-center gap-2 bg-white/10 rounded-full px-3 py-1">
                    <Package className="h-4 w-4" />
                    <span>{filterStats.total} Products</span>
                  </div>
                  {filterStats.activeFilters > 0 && (
                    <div className="flex items-center gap-2 bg-white/10 rounded-full px-3 py-1">
                      <Filter className="h-4 w-4" />
                      <span>{filterStats.activeFilters} Filters Applied</span>
                    </div>
                  )}
                </div>
              </div>
              
              {/* Header Actions */}
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
                {/* View Mode Toggle */}
                <div className="flex items-center bg-white/10 rounded-lg p-1">
                  <button
                    onClick={() => setViewMode('grid')}
                    className={`p-2 rounded-md transition-colors ${viewMode === 'grid' ? 'bg-white text-purple-600' : 'text-white hover:bg-white/10'}`}
                    title="Grid view"
                  >
                    <Grid3X3 className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => setViewMode('list')}
                    className={`p-2 rounded-md transition-colors ${viewMode === 'list' ? 'bg-white text-purple-600' : 'text-white hover:bg-white/10'}`}
                    title="List view"
                  >
                    <List className="h-4 w-4" />
                  </button>
                </div>
                
                {/* Add Product Button */}
                {productPerms.canCreate && (
                  <button
                    onClick={handleCreate}
                    className="bg-white text-purple-600 hover:bg-purple-50 font-semibold px-4 sm:px-6 py-2.5 sm:py-3 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 flex items-center gap-2"
                  >
                    <Plus className="h-4 w-4 sm:h-5 sm:w-5" />
                    <span className="text-sm sm:text-base">Add Product</span>
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Enhanced Filters Section */}
        <div className="mb-6 sm:mb-8">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
            
            {/* Filter Header */}
            <div className="border-b border-gray-200 dark:border-gray-700 px-4 sm:px-6 py-4">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl flex items-center justify-center text-white shadow-lg">
                    <SlidersHorizontal className="h-5 w-5" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Advanced Filters</h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {filterStats.activeFilters > 0 ? `${filterStats.activeFilters} filters active` : 'No filters applied'}
                    </p>
                  </div>
                </div>
                
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setShowFilters(!showFilters)}
                    className="btn-outline px-3 py-2 text-sm flex items-center gap-2"
                  >
                    <SlidersHorizontal className="h-4 w-4" />
                    <span className="hidden sm:inline">{showFilters ? 'Hide' : 'Show'} Filters</span>
                  </button>
                  {filterStats.activeFilters > 0 && (
                    <button
                      onClick={clearFilters}
                      className="btn-secondary px-3 py-2 text-sm flex items-center gap-2"
                    >
                      <X className="h-4 w-4" />
                      <span className="hidden sm:inline">Clear All</span>
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Filter Controls */}
            {showFilters && (
              <div className="p-4 sm:p-6">
                
                {/* Row 1: Search + Quick Actions */}
                <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 mb-6">
                  {/* Enhanced Search */}
                  <div className="lg:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Search Products
                    </label>
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                      <input
                        type="text"
                        placeholder="Search by name, description, or category..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="w-full pl-10 pr-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-200"
                      />
                      {search && (
                        <button
                          onClick={() => setSearch('')}
                          className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Sort Controls */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Sort By
                    </label>
                    <select
                      value={sortBy}
                      onChange={(e) => setSortBy(e.target.value)}
                      className="w-full px-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    >
                      {sortOptions.map(option => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Sort Order
                    </label>
                    <div className="flex rounded-xl overflow-hidden border border-gray-300 dark:border-gray-600">
                      <button
                        onClick={() => setSortOrder('asc')}
                        className={`flex-1 px-3 py-2.5 text-sm font-medium transition-colors ${
                          sortOrder === 'asc'
                            ? 'bg-purple-600 text-white'
                            : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600'
                        }`}
                      >
                        A-Z
                      </button>
                      <button
                        onClick={() => setSortOrder('desc')}
                        className={`flex-1 px-3 py-2.5 text-sm font-medium transition-colors border-l border-gray-300 dark:border-gray-600 ${
                          sortOrder === 'desc'
                            ? 'bg-purple-600 text-white'
                            : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600'
                        }`}
                      >
                        Z-A
                      </button>
                    </div>
                  </div>
                </div>

                {/* Row 2: Hierarchy Filters */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                  {/* Company Filter */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      <Building2 className="h-4 w-4 inline mr-1" />
                      Company
                    </label>
                    <select
                      value={companyFilter}
                      onChange={(e) => handleCompanyChange(e.target.value)}
                      className="w-full px-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    >
                      <option value="">All Companies</option>
                      {companies.map((company) => (
                        <option key={company.uuid} value={company.uuid}>
                          {company.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Category Filter */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      <Tag className="h-4 w-4 inline mr-1" />
                      Category
                    </label>
                    <select
                      value={categoryFilter}
                      onChange={(e) => handleCategoryChange(e.target.value)}
                      className="w-full px-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    >
                      <option value="">All Categories</option>
                      {categories.map((category) => (
                        <option key={category.uuid} value={category.uuid}>
                          {category.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Product Group Filter */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      <Layers3 className="h-4 w-4 inline mr-1" />
                      Product Group
                    </label>
                    <select
                      value={productGroupFilter}
                      onChange={(e) => setProductGroupFilter(e.target.value)}
                      className="w-full px-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-purple-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
                      disabled={!categoryFilter}
                    >
                      <option value="">All Product Groups</option>
                      {productGroups.map((productGroup) => (
                        <option key={productGroup.uuid} value={productGroup.uuid}>
                          {productGroup.name}
                        </option>
                      ))}
                    </select>
                    {!categoryFilter && (
                      <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                        Select a category first
                      </p>
                    )}
                  </div>

                  {/* Price Range Filter */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      <DollarSign className="h-4 w-4 inline mr-1" />
                      Price Range
                    </label>
                    <select
                      value={priceRangeFilter}
                      onChange={(e) => setPriceRangeFilter(e.target.value)}
                      className="w-full px-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    >
                      {priceRanges.map((range) => (
                        <option key={range.value} value={range.value}>
                          {range.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Row 3: Status Filters */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
                  {/* Stock Status */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Stock Status
                    </label>
                    <select
                      value={stockFilter}
                      onChange={(e) => setStockFilter(e.target.value)}
                      className="w-full px-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    >
                      <option value="">All Stock</option>
                      <option value="true">In Stock</option>
                      <option value="false">Out of Stock</option>
                    </select>
                  </div>

                  {/* Active Status */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Active Status
                    </label>
                    <select
                      value={activeFilter}
                      onChange={(e) => setActiveFilter(e.target.value)}
                      className="w-full px-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    >
                      <option value="">All Status</option>
                      <option value="true">Active</option>
                      <option value="false">Inactive</option>
                    </select>
                  </div>

                  {/* Quantity Filter */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Quantity Info
                    </label>
                    <select
                      value={quantityFilter}
                      onChange={(e) => setQuantityFilter(e.target.value)}
                      className="w-full px-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    >
                      {quantityOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Items Per Page */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Items Per Page
                    </label>
                    <select
                      value={itemsPerPage}
                      onChange={(e) => {
                        setItemsPerPage(Number(e.target.value))
                        setCurrentPage(1)
                      }}
                      className="w-full px-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    >
                      {itemsPerPageOptions.map((option) => (
                        <option key={option} value={option}>
                          {option} items
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Active Filters Summary */}
                {filterStats.activeFilters > 0 && (
                  <div className="bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-700 rounded-xl p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 text-purple-700 dark:text-purple-300">
                        <Filter className="h-4 w-4" />
                        <span className="font-medium">{filterStats.activeFilters} active filters</span>
                      </div>
                      <button
                        onClick={clearFilters}
                        className="text-purple-600 hover:text-purple-700 dark:text-purple-400 dark:hover:text-purple-300 font-medium text-sm transition-colors"
                      >
                        Clear all filters
                      </button>
                    </div>
                    {filterStats.total > 0 && (
                      <p className="text-sm text-purple-600 dark:text-purple-400 mt-1">
                        Showing {filterStats.total} matching product{filterStats.total !== 1 ? 's' : ''}
                      </p>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Products Section */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
          
          {/* Loading State */}
          {isLoading && (
            <div className="flex items-center justify-center py-12 sm:py-16">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-2 border-purple-600 border-t-transparent mx-auto mb-4"></div>
                <p className="text-gray-600 dark:text-gray-400 font-medium">Loading products...</p>
              </div>
            </div>
          )}

          {/* Error State */}
          {error && (
            <div className="flex items-center justify-center py-12 sm:py-16">
              <div className="text-center">
                <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Failed to load products</h3>
                <p className="text-gray-600 dark:text-gray-400 mb-4">Please try refreshing the page</p>
                <button
                  onClick={() => queryClient.invalidateQueries('products')}
                  className="btn-primary px-4 py-2"
                >
                  Retry
                </button>
              </div>
            </div>
          )}

          {/* Empty State */}
          {!isLoading && !error && products.length === 0 && (
            <div className="flex items-center justify-center py-12 sm:py-16">
              <div className="text-center">
                <Package className="h-16 w-16 text-gray-400 dark:text-gray-500 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">No products found</h3>
                <p className="text-gray-600 dark:text-gray-400 mb-6">
                  {filterStats.activeFilters > 0 
                    ? 'Try adjusting your filters to find products'
                    : 'Get started by creating your first product'
                  }
                </p>
                {filterStats.activeFilters > 0 ? (
                  <button
                    onClick={clearFilters}
                    className="btn-outline px-4 py-2 mr-3"
                  >
                    Clear Filters
                  </button>
                ) : null}
                {productPerms.canCreate && (
                  <button
                    onClick={handleCreate}
                    className="btn-primary px-6 py-3 inline-flex items-center gap-2"
                  >
                    <Plus className="h-5 w-5" />
                    Create Product
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Products Display */}
          {!isLoading && !error && products.length > 0 && (
            <>
              {/* Products Header */}
              <div className="border-b border-gray-200 dark:border-gray-700 px-4 sm:px-6 py-4">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                      Products ({pagination.total})
                    </h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      Showing {((pagination.page - 1) * pagination.limit) + 1} to{' '}
                      {Math.min(pagination.page * pagination.limit, pagination.total)} of{' '}
                      {pagination.total} products
                    </p>
                  </div>
                  
                  {/* Responsive View Indicators */}
                  <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                    <div className="hidden xl:flex items-center gap-1">
                      <Monitor className="h-3 w-3" />
                      <span>Desktop</span>
                    </div>
                    <div className="hidden lg:flex xl:hidden items-center gap-1">
                      <Tablet className="h-3 w-3" />
                      <span>Tablet</span>
                    </div>
                    <div className="flex lg:hidden items-center gap-1">
                      <Smartphone className="h-3 w-3" />
                      <span>Mobile</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Products Grid/List */}
              <div className="p-4 sm:p-6">
                {viewMode === 'grid' ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-6 gap-4 lg:gap-6">
                    {products.map((product, index) => (
                      <ProductCard
                        key={product.uuid}
                        product={product}
                        index={index}
                        onEdit={handleEdit}
                        onDelete={handleDelete}
                        onToggleStatus={handleToggleStatus}
                        permissions={productPerms}
                      />
                    ))}
                  </div>
                ) : (
                  <div className="space-y-4">
                    {products.map((product, index) => (
                      <ProductListItem
                        key={product.uuid}
                        product={product}
                        index={index}
                        onEdit={handleEdit}
                        onDelete={handleDelete}
                        onToggleStatus={handleToggleStatus}
                        permissions={productPerms}
                      />
                    ))}
                  </div>
                )}
              </div>

              {/* Enhanced Pagination */}
              {pagination.pages > 1 && (
                <div className="border-t border-gray-200 dark:border-gray-700 px-4 sm:px-6 py-4">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div className="text-sm text-gray-700 dark:text-gray-300">
                      Showing <span className="font-medium">{((pagination.page - 1) * pagination.limit) + 1}</span> to{' '}
                      <span className="font-medium">{Math.min(pagination.page * pagination.limit, pagination.total)}</span> of{' '}
                      <span className="font-medium">{pagination.total}</span> results
                    </div>
                    
                    <div className="flex items-center justify-center sm:justify-end space-x-2">
                      <button
                        onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                        disabled={currentPage === 1}
                        className="btn-outline px-3 py-2 text-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                      >
                        <ChevronLeft className="h-4 w-4" />
                        <span className="hidden sm:inline">Previous</span>
                      </button>
                      
                      <div className="flex items-center space-x-1">
                        {Array.from({ length: Math.min(5, pagination.pages) }, (_, i) => {
                          let page;
                          if (pagination.pages <= 5) {
                            page = i + 1;
                          } else if (currentPage <= 3) {
                            page = i + 1;
                          } else if (currentPage >= pagination.pages - 2) {
                            page = pagination.pages - 4 + i;
                          } else {
                            page = currentPage - 2 + i;
                          }
                          
                          return (
                            <button
                              key={page}
                              onClick={() => setCurrentPage(page)}
                              className={`w-10 h-10 rounded-lg text-sm font-medium transition-colors ${
                                currentPage === page
                                  ? 'bg-purple-600 text-white shadow-lg'
                                  : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                              }`}
                            >
                              {page}
                            </button>
                          );
                        })}
                      </div>
                      
                      <button
                        onClick={() => setCurrentPage(Math.min(pagination.pages, currentPage + 1))}
                        disabled={currentPage === pagination.pages}
                        className="btn-outline px-3 py-2 text-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                      >
                        <span className="hidden sm:inline">Next</span>
                        <ChevronRight className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Delete Confirmation Modal */}
        <ConfirmationModal
          isOpen={deleteModalOpen}
          onClose={() => setDeleteModalOpen(false)}
          onConfirm={handleConfirmDelete}
          title="Delete Product"
          message={
            productToDelete
              ? `Are you sure you want to delete "${productToDelete.name}"? This action cannot be undone.`
              : ''
          }
          confirmText="Delete"
          confirmButtonClass="btn-danger"
          isLoading={deleteMutation.isLoading}
        />
      </div>
    </div>
  )
}

// Enhanced Product Card Component
function ProductCard({ product, index, onEdit, onDelete, onToggleStatus, permissions }) {
  return (
    <div 
      className={`group relative bg-white dark:bg-gray-700 rounded-2xl border border-gray-200 dark:border-gray-600 overflow-hidden hover:shadow-2xl hover:-translate-y-2 transition-all duration-300 ${
        !product.is_active ? 'opacity-75' : ''
      }`}
      style={{ animationDelay: `${index * 0.05}s` }}
    >
      {/* Product Image */}
      <div className="aspect-square bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-600 dark:to-gray-700 relative overflow-hidden">
        {product.image_url ? (
          <img
            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
            src={product.image_url}
            alt={product.name}
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Package className="h-12 w-12 text-gray-400 dark:text-gray-500" />
          </div>
        )}
        
        {/* Status Badges */}
        <div className="absolute top-3 right-3 flex flex-col space-y-1">
          <span className={`inline-flex items-center px-2 py-1 text-xs font-medium rounded-full shadow-lg ${
            product.is_active
              ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
              : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
          }`}>
            {product.is_active ? (
              <CheckCircle className="h-3 w-3 mr-1" />
            ) : (
              <XCircle className="h-3 w-3 mr-1" />
            )}
            {product.is_active ? 'Active' : 'Inactive'}
          </span>
          
          <span className={`inline-flex items-center px-2 py-1 text-xs font-medium rounded-full shadow-lg ${
            product.stock_quantity > 0
              ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400'
              : 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400'
          }`}>
            {product.stock_quantity > 0 ? `${product.stock_quantity} in stock` : 'Out of stock'}
          </span>
        </div>

        {/* Action Buttons */}
        <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center space-x-2">
          {permissions.canUpdate && (
            <button
              onClick={() => onEdit(product)}
              className="px-3 py-2 bg-white/90 backdrop-blur-sm rounded-xl hover:bg-white transition-colors shadow-lg flex items-center space-x-1"
              title="Edit product"
            >
              <Edit className="h-4 w-4 text-gray-700" />
              <span className="text-sm font-medium text-gray-700 hidden sm:inline">Edit</span>
            </button>
          )}
          
          {permissions.canDelete && (
            product.is_active ? (
              <button
                onClick={() => onDelete(product)}
                className="px-3 py-2 bg-white/90 backdrop-blur-sm rounded-xl hover:bg-white transition-colors shadow-lg flex items-center space-x-1"
                title="Delete product"
              >
                <Trash2 className="h-4 w-4 text-red-600" />
                <span className="text-sm font-medium text-red-600 hidden sm:inline">Delete</span>
              </button>
            ) : (
              <button
                onClick={() => onToggleStatus(product)}
                className="px-3 py-2 bg-white/90 backdrop-blur-sm rounded-xl hover:bg-white transition-colors shadow-lg flex items-center space-x-1"
                title="Activate product"
              >
                <CheckCircle className="h-4 w-4 text-green-600" />
                <span className="text-sm font-medium text-green-600 hidden sm:inline">Activate</span>
              </button>
            )
          )}
        </div>
      </div>

      {/* Product Info */}
      <div className="p-4">
        <h3 className="font-semibold text-gray-900 dark:text-white text-sm mb-2 line-clamp-2 min-h-[2.5rem]">
          {product.name}
        </h3>
        
        {product.description && (
          <p className="text-gray-600 dark:text-gray-400 text-xs mb-3 line-clamp-2 min-h-[2rem]">
            {product.description}
          </p>
        )}
        
        <div className="flex items-center justify-between mb-3">
          <span className="text-lg font-bold text-purple-600 dark:text-purple-400">
            ₹{parseFloat(product.price).toLocaleString('en-IN')}
          </span>
          {product.quantity && (
            <span className="text-xs text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-600 px-2 py-1 rounded-full">
              {product.quantity}
            </span>
          )}
        </div>
        
        {/* Hierarchy Info */}
        <div className="space-y-1">
          {product.company_name && (
            <div className="flex items-center text-xs text-gray-600 dark:text-gray-400">
              <Building2 className="h-3 w-3 mr-1" />
              <span className="truncate">{product.company_name}</span>
            </div>
          )}
          {product.category_name && (
            <div className="flex items-center text-xs text-gray-600 dark:text-gray-400">
              <Tag className="h-3 w-3 mr-1" />
              <span className="truncate">{product.category_name}</span>
            </div>
          )}
          {product.product_group_name && (
            <div className="flex items-center text-xs text-gray-600 dark:text-gray-400">
              <Layers3 className="h-3 w-3 mr-1" />
              <span className="truncate">{product.product_group_name}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

// Enhanced Product List Item Component  
function ProductListItem({ product, index, onEdit, onDelete, onToggleStatus, permissions }) {
  return (
    <div 
      className={`group bg-white dark:bg-gray-700 rounded-2xl border border-gray-200 dark:border-gray-600 p-4 hover:shadow-xl transition-all duration-300 ${
        !product.is_active ? 'opacity-75' : ''
      }`}
      style={{ animationDelay: `${index * 0.05}s` }}
    >
      <div className="flex items-center space-x-4">
        {/* Product Image */}
        <div className="flex-shrink-0 w-16 h-16 sm:w-20 sm:h-20 bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-600 dark:to-gray-700 rounded-xl overflow-hidden">
          {product.image_url ? (
            <img
              className="w-full h-full object-cover"
              src={product.image_url}
              alt={product.name}
              loading="lazy"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <Package className="h-6 w-6 text-gray-400 dark:text-gray-500" />
            </div>
          )}
        </div>

        {/* Product Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between">
            <div className="flex-1 min-w-0">
              <h3 className="font-semibold text-gray-900 dark:text-white text-sm sm:text-base mb-1 truncate">
                {product.name}
              </h3>
              {product.description && (
                <p className="text-gray-600 dark:text-gray-400 text-xs sm:text-sm mb-2 line-clamp-2">
                  {product.description}
                </p>
              )}
              
              {/* Hierarchy Info - Responsive */}
              <div className="flex flex-wrap items-center gap-2 mb-2">
                {product.company_name && (
                  <span className="inline-flex items-center text-xs text-gray-600 dark:text-gray-400 bg-gray-100 dark:bg-gray-600 px-2 py-1 rounded-full">
                    <Building2 className="h-3 w-3 mr-1" />
                    <span className="hidden sm:inline">{product.company_name}</span>
                    <span className="sm:hidden">{product.company_name.slice(0, 10)}...</span>
                  </span>
                )}
                {product.category_name && (
                  <span className="inline-flex items-center text-xs text-gray-600 dark:text-gray-400 bg-blue-100 dark:bg-blue-900/30 px-2 py-1 rounded-full">
                    <Tag className="h-3 w-3 mr-1" />
                    <span className="hidden sm:inline">{product.category_name}</span>
                    <span className="sm:hidden">{product.category_name.slice(0, 10)}...</span>
                  </span>
                )}
                {product.product_group_name && (
                  <span className="inline-flex items-center text-xs text-gray-600 dark:text-gray-400 bg-purple-100 dark:bg-purple-900/30 px-2 py-1 rounded-full">
                    <Layers3 className="h-3 w-3 mr-1" />
                    <span className="hidden sm:inline">{product.product_group_name}</span>
                    <span className="sm:hidden">{product.product_group_name.slice(0, 10)}...</span>
                  </span>
                )}
              </div>
            </div>

            {/* Product Stats */}
            <div className="flex-shrink-0 text-right ml-4">
              <div className="text-lg sm:text-xl font-bold text-purple-600 dark:text-purple-400 mb-1">
                ₹{parseFloat(product.price).toLocaleString('en-IN')}
              </div>
              {product.quantity && (
                <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                  {product.quantity}
                </div>
              )}
              <div className={`text-xs font-medium ${
                product.stock_quantity > 0
                  ? 'text-green-600 dark:text-green-400'
                  : 'text-red-600 dark:text-red-400'
              }`}>
                {product.stock_quantity > 0 ? `${product.stock_quantity} in stock` : 'Out of stock'}
              </div>
            </div>
          </div>
        </div>

        {/* Status & Actions */}
        <div className="flex-shrink-0 flex flex-col items-end space-y-2">
          {/* Status Badge */}
          <span className={`inline-flex items-center px-2 py-1 text-xs font-medium rounded-full ${
            product.is_active
              ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
              : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
          }`}>
            {product.is_active ? (
              <CheckCircle className="h-3 w-3 mr-1" />
            ) : (
              <XCircle className="h-3 w-3 mr-1" />
            )}
            {product.is_active ? 'Active' : 'Inactive'}
          </span>

          {/* Actions */}
          <div className="flex items-center space-x-1">
            {permissions.canUpdate && (
              <button
                onClick={() => onEdit(product)}
                className="p-2 text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors rounded-lg hover:bg-gray-100 dark:hover:bg-gray-600"
                title="Edit product"
              >
                <Edit className="h-4 w-4" />
              </button>
            )}
            
            {permissions.canDelete && (
              product.is_active ? (
                <button
                  onClick={() => onDelete(product)}
                  className="p-2 text-gray-400 hover:text-red-600 dark:hover:text-red-400 transition-colors rounded-lg hover:bg-gray-100 dark:hover:bg-gray-600"
                  title="Delete product"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              ) : (
                <button
                  onClick={() => onToggleStatus(product)}
                  className="p-2 text-gray-400 hover:text-green-600 dark:hover:text-green-400 transition-colors rounded-lg hover:bg-gray-100 dark:hover:bg-gray-600"
                  title="Activate product"
                >
                  <CheckCircle className="h-4 w-4" />
                </button>
              )
            )}
          </div>
        </div>
      </div>
    </div>
  )
}